/* eslint-disable no-nested-ternary */
import React, { FC, ReactElement, useContext, useEffect, useState } from 'react';
import WidgetInfo from '../WidgetInfo';
import WidgetResult from '../widgetResult/WidgetResult';
import WidgetPlayers from '../WidgetPlayers';
import { getLiveMatch } from '../../services/http/httpService';
import { ITransformFootballMatch } from '../../interfaces/interfaces';
import { LiveMatchContext, PrefferencesContext } from '../../context/index';
import {
  getLanguageOption,
  matchStage,
  getTenantFromHost,
  getMatchIdOption,
  getSportTypeOption,
  getUTCGameTime,
  getUTCFromCET
} from '../../helpers/helperFunctions';
import { REFRESH_INTERVAL } from '../../config/config';
import Loader from '../common/Loader';
import ScorePerPeriod from '../ScorePerPeriod';

const Main: FC = (): ReactElement => {
  const {
    isLoading,
    liveMatchData: { details },
    setLiveMatchData,
    setIsloading,
    matchId,
    setMatchId,
    sportType,
    setSportType
  } = useContext(LiveMatchContext);
  const { setParentHost, language, setLanguage } = useContext(PrefferencesContext);

  const [gameStage, setGameStage] = useState<string>('');
  const [refreshInterval, setRefreshInterval] = useState<number>();
  const [gameDate, setGameDate] = useState<string>('');

  // Load match data
  useEffect(() => {
    if (!matchId || !sportType || !language) return;
    setIsloading(true);
    getLiveMatch<ITransformFootballMatch>(matchId, language, sportType)
      .then((res) => {
        setLiveMatchData(res);
        setGameStage(matchStage(res?.details?.matchEvent, sportType, res?.postponed));
        setIsloading(false);
        if (res.date) {
          setGameDate(res.date);
          const gameDateTimestamp = getUTCGameTime(res.date);
          const currentTimestamp = getUTCFromCET();
          const timeDifference = gameDateTimestamp - currentTimestamp;
          // If the game hasn't started yet
          if (timeDifference > 0) {
            // Start making short polling requests around 5 mins before the game
            const startRefreshingWidgetIn = timeDifference < 300000 ? 0 : timeDifference - 300000;
            // setTimeout(() => {
            //   setRefreshInterval(REFRESH_INTERVAL);
            // }, startRefreshingWidgetIn);
            const blob = new Blob([
              `onmessage = function(e) {setTimeout(() => postMessage(null), ${startRefreshingWidgetIn})}`
            ]);
            const blobURL = window.URL.createObjectURL(blob);
            const worker = new Worker(blobURL);
            worker.onmessage = () => setRefreshInterval(REFRESH_INTERVAL);
            worker.postMessage('start countdown');
            return;
          }
          setRefreshInterval(REFRESH_INTERVAL);
        }
      })
      .catch((err) => {
        console.log('Error getting live match data', err);
        setIsloading(false);
      });
  }, [matchId, language, sportType]);

  // Register interval to re-fetch match
  useEffect(() => {
    if (gameStage === 'game-end' || gameStage === 'postponed') {
      return undefined;
    }

    if (!refreshInterval) return undefined;

    const standingsInterval = setInterval(() => {
      if (gameStage === 'game-end') {
        clearInterval(standingsInterval);
        return;
      }
      getLiveMatch<ITransformFootballMatch>(matchId, language, sportType)
        .then((response) => {
          setLiveMatchData(response);
          const stage = matchStage(response?.details?.matchEvent, sportType, response?.postponed);
          setGameStage(stage);
          if (stage === 'game-end') clearInterval(standingsInterval);
          setIsloading(false);
        })
        .catch((e) => {
          console.log('Error fetching standings');
        });
    }, REFRESH_INTERVAL);

    return () => clearInterval(standingsInterval);
  }, [refreshInterval, matchId, language, sportType]);

  // Determine language queryString option
  useEffect(() => {
    try {
      setLanguage(getLanguageOption());
    } catch (e) {
      console.log('Error processing language option');
    }
  }, []);

  // Determine matchId queryString option
  useEffect(() => {
    try {
      setMatchId(getMatchIdOption());
    } catch (e) {
      console.log('Error processing language option');
    }
  }, []);

  // Determine sportType of the widget, default football
  useEffect(() => {
    try {
      setSportType(getSportTypeOption());
    } catch (e) {
      console.log('Error processing sportType');
    }
  }, []);

  // Determine host of parent window (to set up appropriate fonts)
  useEffect(() => {
    try {
      setParentHost(getTenantFromHost());
    } catch (e) {
      console.log('Error processing parent host');
    }
  }, []);

  return (
    <div className={`widget widget-${sportType}`}>
      {isLoading ? (
        <Loader sportType={sportType} />
      ) : (
        <>
          <WidgetInfo gameStage={gameStage} gameDate={gameDate} />
          <WidgetResult gameStage={gameStage} />
          {sportType === 'icehockey' && !!gameStage && (
            <ScorePerPeriod score={details?.score} gameEnded={gameStage === 'game-end'} />
          )}
          <WidgetPlayers sportType={sportType} />
        </>
      )}
    </div>
  );
};

export default Main;
